'use client';

import React, { useState } from 'react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shadcn/ui/form';
import { PhoneInput } from '@/shadcn/ui/phone-input';
import JoinWaitlistButton from '@/components/global/ui/JoinWaitlistButton';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { useWaitlistForm } from '@/hooks/useWaitlistForm';
import FormSuccessStep from '@/components/pageComponents/global/FormSuccessStep';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shadcn/ui/dialog';
import Image from 'next/image';
import PopupWatchImage from '@/public/global/popup-watch.png';


export default function FooterForm({ className }: { className?: string }) {
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const { form, loading, handleFormStarted, handleEmailBlur, onSubmit } =
    useWaitlistForm({
      formName: 'footer_waitlist',
      onSuccess: () => setShowSuccessDialog(true),
    });

  return (
    <>
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="!p-0 overflow-clip rounded-[1rem] !w-[min(170rem,calc(100%-4rem))] max-w-[calc(100%-3rem)]">
          <div className="w-full bg-black flex flex-col justify-center items-start px-[2rem] lg:px-[8rem] overflow-clip py-[4rem] lg:py-[10rem] relative lg:min-h-[100rem]">
            <div className="lg:max-w-[70rem] flex flex-col gap-[6rem] lg:py-0 py-[5rem]">
              <FormSuccessStep className="lg:items-start items-center" />
            </div>
            <div className="lg:w-[100rem] lg:block hidden w-full h-auto absolute lg:top-0 top-auto bottom-0 lg:bottom-auto right-0 lg:right-[3rem]">
              <Image
                src={PopupWatchImage}
                alt="Popup Watch"
                className="!w-full !h-full !object-contain"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    <div
      className={cn(
        'w-full max-w-[73rem] p-[3rem] rounded-[0.5rem] border-[.125rem] border-[rgba(255,255,255,0.08)]',
        className,
      )}
    >
      <Form {...form}>
        <form
          className="w-full flex flex-col justify-start items-stretch gap-[2rem]"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[2rem]">
            {/* Name Field */}
            <FormField
              control={form.control}
              name="name"
              disabled={loading}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <input
                      type="text"
                      placeholder="Name"
                      {...field}
                      onFocus={() => handleFormStarted()}
                      className="w-full rounded-[0.5rem] border-[.125rem] border-[rgba(255,255,255,0.16)] bg-black h-[5.375rem] placeholder:text-offwhite placeholder:opacity-70 text-[2rem] text-offwhite font-nunito focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-offset-black focus:ring-white px-[2rem]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email Field */}
            <FormField
              control={form.control}
              name="email"
              disabled={loading}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <input
                      type="email"
                      placeholder="Email"
                      {...field}
                      onFocus={() => handleFormStarted()}
                      onBlur={field.onBlur}
                      className="w-full rounded-[0.5rem] border-[.125rem] border-[rgba(255,255,255,0.16)] bg-black h-[5.375rem] placeholder:text-offwhite placeholder:opacity-70 text-[2rem] text-offwhite font-nunito focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-offset-black focus:ring-white px-[2rem]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Phone Field */}
          <FormField
            control={form.control}
            name="phone"
            disabled={loading}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PhoneInput
                    placeholder="Phone number"
                    defaultCountry="DE"
                    value={String(field.value || '')}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={loading}
                    className="w-full rounded-[0.5rem] border-[.125rem] border-[rgba(255,255,255,0.16)] bg-black h-[5.375rem] placeholder:text-offwhite placeholder:opacity-70 text-[2rem] text-offwhite font-nunito focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-offset-black focus:ring-white"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Terms Notice */}
          <div className="text-[1.5rem] leading-[120%] text-offwhite/70 font-nunito font-[300]">
            By clicking on 'Join the release waitlist' you agree to receive
            information and accept our{' '}
            <Link
              href="/terms-conditions"
              className="underline hover:opacity-70 transition-opacity"
            >
              Terms & Conditions
            </Link>{' '}
            and{' '}
            <Link
              href="/privacy-policy"
              className="underline hover:opacity-70 transition-opacity"
            >
              Privacy Policy
            </Link>
            .
          </div>

          {/* Submit Button */}
          <JoinWaitlistButton type="submit" disabled={loading}>
            {loading ? 'Joining...' : 'Join the release waitlist'}
          </JoinWaitlistButton>
        </form>
      </Form>
    </div>
    </>
  );
}
